import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Print } from '@mui/icons-material';
import { useRouter } from '../contexts/RouterContext';
import { useAuth } from '../contexts/AuthContext';
import {
  patientTherapyPlanService, TherapyGoalRow, TherapyPeriodRow,
} from '../services/patientTherapyPlanService';
import { patientsService, PatientDetail } from '../services/patientsService';
import { printTemplateService, renderTemplateHtml, PrintTemplate } from '../services/printTemplateService';
import { PrintTemplateBar, usePrintTemplateChoice } from './print/PrintTemplateChoice';
import { colors } from '../theme/designSystem';

/**
 * Impressão de um objetivo do plano terapêutico (RN-541g): o objetivo por inteiro, a régua de
 * 0 a 10, o andamento das sessões e — quando houver — a conclusão assinada com nome e documento
 * profissional de quem a firmou.
 *
 * Cabeçalho e rodapé vêm do TEMPLATE PADRÃO da entidade e se repetem em todas as páginas por
 * thead/tfoot, como nas demais impressões (RN-534e): o navegador repete o grupo E reserva a
 * altura dele em cada folha.
 */
const formatarData = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

const TherapyGoalPrint: React.FC = () => {
  const { getParam } = useRouter();
  const { user } = useAuth();
  const goalId = getParam('goalId') || '';
  const patientId = getParam('patientId') || '';

  const [goal, setGoal] = useState<TherapyGoalRow | null>(null);
  const [period, setPeriod] = useState<TherapyPeriodRow | null>(null);
  const [patient, setPatient] = useState<PatientDetail | null>(null);
  const [templates, setTemplates] = useState<PrintTemplate[]>([]);
  const templateChoice = usePrintTemplateChoice(templates, 'therapy-goal');
  const template = templateChoice.template;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Clinic4Us - Impressão do Plano Terapêutico';
    const load = async () => {
      try {
        // O objetivo vem da mesma consulta da aba: uma fonte só para tela e papel
        const data = await patientTherapyPlanService.getByPatient(patientId, { pageSize: 100 });
        const todos = [...data.periods.flatMap(p => p.goals), ...data.goalsWithoutPeriod];
        const alvo = todos.find(g => g.id === goalId) ?? null;
        setGoal(alvo);
        setPeriod(data.periods.find(p => p.goals.some(g => g.id === goalId)) ?? null);

        const [patientData, templates] = await Promise.all([
          patientsService.getById(patientId).catch(() => null),
          // Sem template (ou sem permissão), imprime sem cabeçalho/rodapé — não falha
          user?.entityId
            ? printTemplateService.getByEntity(user.entityId).catch(() => [])
            : Promise.resolve<PrintTemplate[]>([]),
        ]);
        setPatient(patientData);
        setTemplates(templates);

        if (!alvo) setError('Objetivo não encontrado.');
      } catch {
        setError('Não foi possível carregar o plano.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [goalId, patientId, user?.entityId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: colors.primary }} />
      </Box>
    );
  }

  if (error || !goal) {
    return (
      <Typography sx={{ color: colors.error, textAlign: 'center', py: 8 }}>
        {error || 'Objetivo não encontrado.'}
      </Typography>
    );
  }

  const pct = (v: number) => `${Math.min(Math.max(v, 0), 10) * 10}%`;

  const linha = (rotulo: string, valor: React.ReactNode) => (
    <tr>
      <td style={{ padding: '2mm 3mm', border: '1px solid #ccc', width: '38mm', fontWeight: 600, verticalAlign: 'top' }}>
        {rotulo}
      </td>
      <td style={{ padding: '2mm 3mm', border: '1px solid #ccc', whiteSpace: 'pre-wrap' }}>
        {valor ?? '—'}
      </td>
    </tr>
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '2rem', '@media print': { padding: 0, backgroundColor: 'white' } }}>
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          .therapy-print-page {
            padding: 0 !important;
            width: auto !important;
            min-height: auto !important;
            box-shadow: none !important;
          }
          .print-frame { width: 100%; border-collapse: collapse; }
          .print-frame > thead { display: table-header-group; }
          .print-frame > tfoot { display: table-footer-group; }
          .print-frame > thead > tr > td,
          .print-frame > tbody > tr > td,
          .print-frame > tfoot > tr > td { padding: 0; }
          .print-frame-header img { max-height: 18mm !important; }
          tr { break-inside: avoid; }
        }
      `}</style>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, maxWidth: '210mm', mx: 'auto', '@media print': { display: 'none' } }}>
        <PrintTemplateBar choice={templateChoice} />
        <Button
          variant="contained"
          startIcon={<Print />}
          onClick={() => window.print()}
          sx={{ backgroundColor: colors.primary, textTransform: 'none', '&:hover': { backgroundColor: '#029AA8' } }}
        >
          Imprimir
        </Button>
      </Box>

      <Box
        className="therapy-print-page"
        sx={{
          width: '210mm',
          maxWidth: '100%',
          minHeight: '297mm',
          padding: '15mm',
          paddingBottom: '30mm',
          boxSizing: 'border-box',
          background: 'white',
          margin: '0 auto',
          position: 'relative',
          boxShadow: '0 0 8px rgba(0,0,0,0.15)',
          fontSize: '12pt',
          color: '#000',
          '@media print': { boxShadow: 'none', width: 'auto', minHeight: 'auto', margin: 0 },
        }}
      >
        <table className="print-frame">
          <thead><tr><td>
            {template && (
<div className="print-frame-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '6mm' }}>
                <div style={{ flexShrink: 0 }}>
                  {template?.file ? (
                    <img src={template.file} alt="logo" style={{ maxHeight: '30mm', maxWidth: '70mm', objectFit: 'contain' }} />
                  ) : null}
                </div>
                <div
                  style={{ textAlign: 'right', fontSize: '10pt', lineHeight: 1.5 }}
                  dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template?.header) }}
                />
              </div>
              <hr style={{ border: 'none', borderTop: '1px solid #999', margin: '6mm 0' }} />
            </div>
)}
          </td></tr></thead>

          <tfoot><tr><td>
            {template && (
<div
              style={{ textAlign: 'center', fontSize: '9pt', paddingTop: '6mm' }}
              dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template?.footer) }}
            />
)}
          </td></tr></tfoot>

          <tbody><tr><td>
            <Box sx={{ textAlign: 'center', mb: '6mm' }}>
              <Typography sx={{ fontSize: '13pt', fontWeight: 700, color: '#212529' }}>
                Plano Terapêutico
              </Typography>
              <Typography sx={{ fontSize: '10pt', color: '#495057' }}>
                {goal.title || 'Objetivo sem título'}
              </Typography>
            </Box>

            <Box sx={{ mb: '6mm' }}>
              <Box sx={{ display: 'flex', gap: '3rem', mb: '4px' }}>
                <Typography sx={{ fontSize: '11px', color: '#212529' }}>
                  Paciente: {patient?.patientName || '—'}
                </Typography>
                <Typography sx={{ fontSize: '11px', color: '#212529' }}>
                  Data Nasc.: {patient?.patientBirthDate ? formatarData(patient.patientBirthDate) : '—'}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '11px', color: '#212529', mb: '4px' }}>
                Responsável: {patient?.responsibleName1 || '—'}
              </Typography>
              <Typography sx={{ fontSize: '11px', color: '#212529', mb: '4px' }}>
                Período: {period?.title || 'sem período'}
                {period && ` (${formatarData(period.initialDate)} a ${formatarData(period.finalDate)})`}
              </Typography>
              <Typography sx={{ fontSize: '11px', color: '#212529' }}>
                Data de emissão: {new Date().toLocaleDateString('pt-BR')}
              </Typography>
            </Box>

            {/* A régua, no mesmo desenho da tela */}
            <Box sx={{ mb: '5mm' }}>
              <Box sx={{ position: 'relative', height: 10, borderRadius: 5, backgroundColor: '#e9ecef', border: '1px solid #ccc' }}>
                {goal.lastEvaluation !== null && (
                  <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: pct(goal.lastEvaluation), backgroundColor: '#03B4C6' }} />
                )}
                {goal.currentScale !== null && (
                  <Box sx={{ position: 'absolute', left: pct(goal.currentScale), top: -3, width: 2, height: 16, backgroundColor: '#6c757d' }} />
                )}
                {goal.targetScale !== null && (
                  <Box sx={{ position: 'absolute', left: pct(goal.targetScale), top: -3, width: 2, height: 16, backgroundColor: '#28a745' }} />
                )}
              </Box>
              <Typography sx={{ fontSize: '10pt', color: '#495057', mt: '2mm' }}>
                Estado atual: <strong>{goal.currentScale ?? '—'}</strong> · Meta:{' '}
                <strong>{goal.targetScale ?? '—'}</strong> · Última avaliação:{' '}
                <strong>{goal.lastEvaluation ?? 'sem registro'}</strong>
              </Typography>
            </Box>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10pt', marginBottom: '5mm' }}>
              <tbody>
                {linha('Objetivo', goal.goal)}
                {linha('Justificativa', goal.justify)}
                {linha('Como medir', goal.metric)}
                {linha('Sessões', goal.sessionsQuantity
                  ? `${goal.sessionsDone} realizada(s) de ${goal.sessionsQuantity} previstas`
                  : `${goal.sessionsDone} realizada(s)`)}
                {linha('Avaliações', goal.evaluationCount === 0
                  ? 'Nenhuma sessão avaliou este objetivo'
                  : `${goal.evaluationCount} sessão(ões) · última em ${formatarData(goal.lastEvaluationDate)}`)}
                {linha('Situação', goal.status)}
                {linha('Prioridade', goal.priority)}
                {linha('Responsável', goal.professionalNames.join(', ') || null)}
                {linha('Equipe', goal.teamName)}
                {linha('Diagnóstico (CID)', goal.diagnosisLabel)}
                {goal.notes ? linha('Observações', goal.notes) : null}
              </tbody>
            </table>

            {goal.conclusion && (
              <Box sx={{ mt: '5mm', border: '1px solid #28a745', borderRadius: '2mm', p: '3mm' }}>
                <Typography sx={{ fontSize: '11pt', fontWeight: 700, color: '#155724', mb: '2mm' }}>
                  Conclusão — nota final {goal.conclusion.finalScale ?? '—'}
                </Typography>
                <Typography sx={{ fontSize: '10pt', color: '#212529', whiteSpace: 'pre-wrap', mb: '4mm' }}>
                  {goal.conclusion.notes}
                </Typography>

                {/* Assinatura: nome e registro do conselho de quem concluiu */}
                <Box sx={{ mt: '10mm', textAlign: 'center' }}>
                  <Box sx={{ borderTop: '1px solid #333', width: '80mm', mx: 'auto', mb: '1mm' }} />
                  <Typography sx={{ fontSize: '10pt', color: '#212529', fontWeight: 600 }}>
                    {goal.conclusion.professionalName || '—'}
                  </Typography>
                  {goal.conclusion.professionalDocument && (
                    <Typography sx={{ fontSize: '9pt', color: '#495057' }}>
                      {goal.conclusion.professionalDocument}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: '9pt', color: '#6c757d' }}>
                    Concluído em {formatarData(goal.conclusion.concludedAt)}
                  </Typography>
                </Box>
              </Box>
            )}
          </td></tr></tbody>
        </table>
      </Box>
    </Box>
  );
};

export default TherapyGoalPrint;
